export type TextRun = { text: string; font: string; color: string; bold?: boolean; italic?: boolean }
const colors = { black: '#000000', dark_blue: '#0000aa', dark_green: '#00aa00', dark_aqua: '#00aaaa', dark_red: '#aa0000', dark_purple: '#aa00aa', gold: '#ffaa00', gray: '#aaaaaa', dark_gray: '#555555', blue: '#5555ff', green: '#55ff55', aqua: '#55ffff', red: '#ff5555', light_purple: '#ff55ff', yellow: '#ffff55', white: '#ffffff' }

function componentValue(value: any): any {
  if (value?.json) return componentValue(value.json)
  if (value?.type && 'value' in value) {
    if (value.type === 'compound') return Object.fromEntries(Object.entries(value.value).map(([key, item]) => [key, componentValue(item)]))
    if (value.type === 'list') return (value.value?.value ?? value.value).map(componentValue)
    return value.value
  }
  if (Array.isArray(value)) return value.map(componentValue)
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, componentValue(item)]))
  return value
}

export function flattenText(component: unknown, inherited: Omit<TextRun, 'text'> = { font: 'minecraft:default', color: '#ffffff' }): TextRun[] {
  let value = componentValue(component)
  if (typeof value === 'string') {
    if (value.startsWith('{') || value.startsWith('[')) {
      try { return flattenText(JSON.parse(value), inherited) } catch {}
    }
    return [{ ...inherited, text: value }]
  }
  if (Array.isArray(value)) return value.flatMap(item => flattenText(item, inherited))
  if (!value || typeof value !== 'object') return []
  const style = { ...inherited }
  for (const key of ['font', 'color', 'bold', 'italic'] as const) if (value[key] !== undefined) (style as any)[key] = value[key]
  const text = value.text ?? value.fallback ?? value.translate ?? ''
  return [...(text ? [{ ...style, text: String(text) }] : []), ...(value.with ?? []).flatMap((item: any) => flattenText(item, style)), ...(value.extra ?? []).flatMap((item: any) => flattenText(item, style))]
}

export function itemLabel(item: any) {
  if (!item) return 'Empty hand'
  const metadata = componentValue(item.nbt)
  const customName = metadata?.display?.Name ?? item.components?.find((component: any) => ['custom_name', 'item_name', 'minecraft:custom_name', 'minecraft:item_name'].includes(component.type))?.data
  return (customName && flattenText(customName).map(run => run.text).join('')) || item.displayName || item.name
}

export function resourceAssetPath(id: string, directory: string) {
  const [namespace, path] = id.includes(':') ? id.split(':') : ['minecraft', id]
  if (!/^[a-z0-9_.-]+$/.test(namespace) || !/^[a-zA-Z0-9_./-]+$/.test(path) || path.split('/').includes('..')) throw new Error('Invalid resource path')
  return `/jerpg-pack/assets/${namespace}/${directory}/${path}`
}

export const bitmapAdvance = (inkWidth: number, height: number, sourceHeight: number) => Math.round(inkWidth * height / sourceHeight) + 1

type Glyph = { advance: number; image?: HTMLCanvasElement; ascent?: number; height?: number }
type Ttf = { family: string; size: number; shift: number[]; skip: string }
type Font = { glyphs: Map<string, Glyph>; ttf?: Ttf; references: string[] }

/** Minecraft font providers drawn directly into a canvas; no DOM screenshot or foreignObject. */
export class XRTextRenderer {
  private fonts = new Map<string, Font>()
  private loading = new Map<string, Promise<void>>()
  private tintCache = new Map<string, HTMLCanvasElement>()
  readonly failures = new Map<string, string>()

  async prepare(component: unknown) {
    await Promise.all([...new Set(flattenText(component).map(run => run.font))].map(font => this.load(font)))
  }

  private load(id: string): Promise<void> {
    if (this.loading.has(id)) return this.loading.get(id)!
    const pending = this.loadFont(id).then(() => { this.failures.delete(id) }).catch(error => { this.failures.set(id, String(error)) })
    this.loading.set(id, pending)
    return pending
  }

  private async loadFont(id: string) {
    const response = await fetch(resourceAssetPath(`${id}.json`, 'font'))
    if (!response.ok) {
      if (id === 'minecraft:default') return
      throw new Error(`Font ${id}: HTTP ${response.status}`)
    }
    const definition = await response.json()
    const font: Font = { glyphs: new Map(), references: [] }
    this.fonts.set(id, font)
    for (const provider of definition.providers ?? []) {
      if (provider.type === 'space') {
        for (const [char, advance] of Object.entries(provider.advances)) if (!font.glyphs.has(char)) font.glyphs.set(char, { advance: Number(advance) })
      } else if (provider.type === 'bitmap') {
        const image = await this.image(resourceAssetPath(provider.file, 'textures'))
        const rows = provider.chars.map((row: string) => Array.from(row)) as string[][]
        const cellW = image.width / rows[0].length
        const cellH = image.height / rows.length
        for (let row = 0; row < rows.length; row++) for (let col = 0; col < rows[row].length; col++) {
          const char = rows[row][col]
          if (char === '\0' || font.glyphs.has(char)) continue
          const canvas = document.createElement('canvas')
          canvas.width = cellW; canvas.height = cellH
          const ctx = canvas.getContext('2d')!
          ctx.drawImage(image, col * cellW, row * cellH, cellW, cellH, 0, 0, cellW, cellH)
          const data = ctx.getImageData(0, 0, cellW, cellH).data
          let inkWidth = 0
          for (let y = 0; y < cellH; y++) for (let x = inkWidth; x < cellW; x++) if (data[(y * cellW + x) * 4 + 3] > 0) inkWidth = x + 1
          const height = provider.height ?? 8
          font.glyphs.set(char, { image: canvas, advance: bitmapAdvance(inkWidth, height, cellH), height, ascent: provider.ascent })
        }
      } else if (provider.type === 'ttf' && !font.ttf) {
        const family = `jerpg-xr-${id.replace(/[^a-z0-9]/gi, '-')}`
        const face = new FontFace(family, `url("${resourceAssetPath(provider.file, 'font')}")`)
        await face.load()
        document.fonts.add(face)
        font.ttf = { family, size: provider.size ?? 11, shift: provider.shift ?? [0, 0], skip: provider.skip ?? '' }
      } else if (provider.type === 'reference') {
        font.references.push(provider.id)
        if (provider.id !== id) await this.load(provider.id)
      }
    }
  }

  private image(url: string) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => reject(new Error(`Cannot load ${url}`))
      image.src = url
    })
  }

  private resolve(id: string, char: string, seen = new Set<string>()): { glyph?: Glyph; ttf?: Ttf } {
    if (seen.has(id)) return {}
    seen.add(id)
    const font = this.fonts.get(id)
    if (font?.glyphs.has(char)) return { glyph: font.glyphs.get(char) }
    if (font?.ttf && !font.ttf.skip.includes(char) && char.codePointAt(0)! < 0xe000) return { ttf: font.ttf }
    for (const ref of font?.references ?? []) {
      const resolved = this.resolve(ref, char, seen)
      if (resolved.glyph || resolved.ttf) return resolved
    }
    return {}
  }

  draw(ctx: CanvasRenderingContext2D, component: unknown, x: number, y: number, draw = true, hiddenFonts: readonly string[] = []) {
    const initialX = x
    ctx.save()
    ctx.imageSmoothingEnabled = false
    ctx.textBaseline = 'alphabetic'
    for (const run of flattenText(component)) {
      const color = colors[run.color as keyof typeof colors] ?? run.color
      for (const char of Array.from(run.text)) {
        const { glyph, ttf } = this.resolve(run.font, char)
        if (glyph) {
          if (draw && glyph.image && !hiddenFonts.includes(run.font)) {
            let image = glyph.image
            if (color !== '#ffffff' && color !== 'white' && color !== '#fff') {
              const key = `${run.font}:${char}:${color}`
              if (!this.tintCache.has(key)) {
                const tinted = document.createElement('canvas')
                tinted.width = image.width; tinted.height = image.height
                const tint = tinted.getContext('2d')!
                tint.drawImage(image, 0, 0)
                tint.globalCompositeOperation = 'multiply'; tint.fillStyle = color; tint.fillRect(0, 0, image.width, image.height)
                tint.globalCompositeOperation = 'destination-in'; tint.drawImage(image, 0, 0)
                this.tintCache.set(key, tinted)
              }
              image = this.tintCache.get(key)!
            }
            ctx.drawImage(image, x, y + 7 - (glyph.ascent ?? 7), image.width * glyph.height! / image.height, glyph.height!)
          }
          x += glyph.advance
        } else {
          ctx.font = `${run.italic ? 'italic ' : ''}${run.bold ? 'bold ' : ''}${ttf?.size ?? 8}px ${ttf ? `"${ttf.family}"` : 'monospace'}`
          ctx.fillStyle = color
          const missing = char.codePointAt(0)! >= 0xe000
          if (missing && run.font.startsWith('jerpg:')) this.failures.set(run.font, `Missing glyph U+${char.codePointAt(0)!.toString(16)} in ${run.font}`)
          if (draw && !hiddenFonts.includes(run.font)) ctx.fillText(missing ? '□' : char, x + (ttf?.shift[0] ?? 0), y + 7 + (ttf?.shift[1] ?? 0))
          // Vanilla rounds TrueType advances up to whole GUI pixels. VT323 at
          // 12px measures 4.8 CSS pixels but must advance 5 for HudCanvas spacing.
          x += ttf ? Math.ceil(ctx.measureText(char).width - 0.0001) : 6
        }
      }
    }
    ctx.restore()
    return x - initialX
  }

  measure(ctx: CanvasRenderingContext2D, component: unknown) { return this.draw(ctx, component, 0, 0, false) }
}

export const xrText = new XRTextRenderer()
